/*
  Back-in-stock trigger for the PDP.

  The Back in Stock app (app embed `amp-back-in-stock`) owns the modal, the email validation, the confirmation and
  the subscriber record; it exposes a global `BIS`. Its own inline button is switched off in the app config
  (`BIS.Config.button.visible === false`) and carries the app's styling rather than the theme's, so the theme
  renders the trigger itself — see snippets/notify-me-button.liquid — and this element only opens the app's popup
  for the variant the customer is looking at.

  `BIS` arrives from a third-party script with no load event of its own, hence the poll. The promise is shared by
  every instance on the page so the sticky bar and the main CTA wait once.
*/

const BIS_POLL_INTERVAL = 100;
const BIS_READY_TIMEOUT = 8000;
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

let bisReady;

function whenBisReady() {
  bisReady ??= new Promise((resolve) => {
    const deadline = Date.now() + BIS_READY_TIMEOUT;

    const poll = () => {
      if (window.BIS?.popup?.ready) {
        window.BIS.popup.ready.then(() => resolve(window.BIS)).catch(() => resolve(null));
        return;
      }

      if (Date.now() > deadline) {
        resolve(null);
        return;
      }

      setTimeout(poll, BIS_POLL_INTERVAL);
    };

    poll();
  });

  return bisReady;
}

class NotifyMeButton extends HTMLElement {
  #abortController;
  #dismissController;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.addEventListener('click', this.#handleClick, { signal: this.#abortController.signal });
    this.#reconcileWithApp();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#dismissController?.abort();
  }

  get #button() {
    return this.querySelector('button');
  }

  get #variantId() {
    return Number(this.getAttribute('variant-id'));
  }

  /*
    `selectVariant` only moves the variant dropdown inside the app's iframe; `show` is what opens the modal. The
    legacy PDP got away with calling `selectVariant` alone because it named its button `#BIS_trigger`, an id the
    app binds its own click handler to — a single-instance trick that would break here, where the buy area and the
    sticky bar each render a trigger.
  */
  #handleClick = async (event) => {
    if (!event.target.closest('button')) return;

    event.preventDefault();

    const form = (await whenBisReady())?.popup?.form;

    if (!form) return;

    form.selectVariant(this.#variantId);
    form.show();
    this.#adoptDialog(form);
  };

  /*
    The app gives its dialog no title, no Escape and no focus containment, so the theme supplies all three. It can:
    the form is built into an `about:blank` iframe, which is same-origin, so its document is scriptable from here.

    Escape and Tab are handled inside that document as well as on the page — once focus is in the frame, keystrokes
    never reach the parent.
  */
  #adoptDialog(form) {
    const frame = form.frame;

    if (!frame) return;

    const dialogTitle = this.getAttribute('dialog-title');

    if (dialogTitle && !frame.title) frame.title = dialogTitle;

    this.#dismissController?.abort();
    this.#dismissController = new AbortController();

    const dismissController = this.#dismissController;
    const options = { signal: dismissController.signal, capture: true };

    /*
      The keystroke is consumed, which is what a dialog is supposed to do with its own Escape: when focus sits in
      the parent document the theme's variant picker also listens for Escape, and letting the event through closed
      the modal *and* reset the selected variant.
    */
    const dismiss = (event) => {
      if (event.key !== 'Escape' || !form.isOpen) return;

      event.preventDefault();
      event.stopPropagation();

      form.hide();
      dismissController.abort();
      this.#button?.focus();
    };

    document.addEventListener('keydown', dismiss, options);

    const frameDocument = frame.contentDocument;

    if (!frameDocument) return;

    frameDocument.addEventListener('keydown', dismiss, options);

    /*
      Focus containment. Tab inside an iframe walks that document's own tab order and then escapes into the parent
      page, which here is still visible around the dialog — so the last element wraps to the first and the first
      wraps back to the last. Queried per open because the app rebuilds the form's markup each time.
    */
    const focusablesOf = () =>
      [...frameDocument.querySelectorAll(FOCUSABLE_SELECTOR)].filter((element) => element.offsetParent !== null);

    frameDocument.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Tab') return;

        const focusables = focusablesOf();

        if (focusables.length === 0) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = frameDocument.activeElement;

        if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      },
      options
    );

    focusablesOf()[0]?.focus();
  }

  /*
    Falls back to the theme's inert "Sold out" whenever the app cannot actually take the subscription — the script
    never loaded, or the variant is not one the app offers alerts for. A button captioned "Notify me" that does
    nothing on click is worse than the state it replaced, for keyboard and screen reader users most of all.
  */
  async #reconcileWithApp() {
    const bis = await whenBisReady();
    const button = this.#button;

    if (!button) return;

    const subscribable = bis?.popup?.variants?.some((variant) => variant.id === this.#variantId);

    if (!subscribable) {
      const fallbackLabel = this.getAttribute('fallback-label');

      if (fallbackLabel) button.textContent = fallbackLabel;
      button.disabled = true;
      return;
    }

    const caption = bis.Config?.button?.caption?.trim();

    if (caption && caption.toLowerCase() !== button.textContent.trim().toLowerCase()) {
      button.textContent = caption;
    }
  }
}

if (!customElements.get('notify-me-button')) {
  customElements.define('notify-me-button', NotifyMeButton);
}
